import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const categories = [
  { id: 1, name: "Ephemera & Papers", description: "Vintage papers, book pages, sheet music, maps, and decorative ephemera packs", count: 284, icon: "ri-file-paper-2-line", image: "https://public.readdy.ai/ai/img_res/a52b74d47530ad97bd005cdb086f7984.jpg" },
  { id: 2, name: "Washi Tape", description: "Decorative washi tapes in every pattern — floral, vintage, geometric, and seasonal", count: 196, icon: "ri-scissors-cut-line", image: "https://readdy.ai/api/search-image?query=colorful%20washi%20tape%20rolls%20collection%20flat%20lay%20craft%20supplies%20pastel%20tones%20aesthetic&width=300&height=200&seq=cat2&orientation=landscape" },
  { id: 3, name: "Stamps & Ink", description: "Rubber stamps, clear stamps, ink pads, and embossing powders for journaling", count: 153, icon: "ri-ink-bottle-line", image: "https://readdy.ai/api/search-image?query=rubber%20stamps%20ink%20pads%20craft%20supplies%20close%20up%20warm%20tones%20artistic%20flat%20lay&width=300&height=200&seq=cat3&orientation=landscape" },
  { id: 4, name: "Stickers & Die Cuts", description: "Watercolor stickers, foil die cuts, and decorative labels for embellishing", count: 312, icon: "ri-star-line", image: "https://readdy.ai/api/search-image?query=watercolor%20stickers%20die%20cuts%20decorative%20labels%20craft%20supplies%20colorful%20flat%20lay%20aesthetic&width=300&height=200&seq=cat4&orientation=landscape" },
  { id: 5, name: "Snail Mail Kits", description: "Complete pen pal kits, letter sets, envelopes, and postage-inspired accessories", count: 127, icon: "ri-mail-line", image: "https://readdy.ai/api/search-image?query=snail%20mail%20pen%20pal%20kit%20envelopes%20letter%20sets%20stationery%20flat%20lay%20warm%20cream%20tones&width=300&height=200&seq=cat5&orientation=landscape" },
  { id: 6, name: "Journal Kits & Bundles", description: "Pre-assembled junk journal kits with papers, tags, pockets, and embellishments", count: 89, icon: "ri-book-open-line", image: "https://readdy.ai/api/search-image?query=junk%20journal%20kit%20bundle%20papers%20tags%20pockets%20embellishments%20flat%20lay%20vintage%20aesthetic&width=300&height=200&seq=cat6&orientation=landscape" },
  { id: 7, name: "Fabric & Textiles", description: "Fabric scraps, lace, ribbon, and textile embellishments for mixed media", count: 74, icon: "ri-scissors-2-line", image: "https://readdy.ai/api/search-image?query=fabric%20scraps%20lace%20ribbon%20textile%20embellishments%20craft%20supplies%20warm%20earthy%20tones&width=300&height=200&seq=cat7&orientation=landscape" },
  { id: 8, name: "Tools & Supplies", description: "Bone folders, punches, adhesives, and essential tools for the craft", count: 61, icon: "ri-tools-line", image: "https://readdy.ai/api/search-image?query=craft%20tools%20bone%20folder%20punches%20adhesives%20supplies%20flat%20lay%20warm%20tones%20artisan&width=300&height=200&seq=cat8&orientation=landscape" },
];

export default function CategoriesPage() {
  const [searchParams] = useSearchParams();
  const initialCat = searchParams.get("cat") || null;
  const [selected, setSelected] = useState<string | null>(initialCat);

  useEffect(() => {
    document.title = "Categories — Junk Journal & Snail Mail Supplies | Junk & Post";
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", "Explore curated categories of junk journal and snail mail supplies. Browse ephemera, washi tape, stamps, stickers, pen pal kits, and more from independent creators.");
    setMeta("og:title", "Categories — Junk Journal & Snail Mail Supplies | Junk & Post", true);
    setMeta("og:description", "Explore curated categories of junk journal and snail mail supplies. Browse ephemera, washi tape, stamps, stickers, and pen pal kits.", true);
    setMeta("og:url", `${SITE_URL}/categories`, true);

    const existing = document.getElementById("schema-categories");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = "schema-categories";
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Junk Journal & Snail Mail Supply Categories",
      "description": "Explore curated categories of junk journal and snail mail supplies from independent creators.",
      "url": `${SITE_URL}/categories`,
      "numberOfItems": categories.length,
      "itemListElement": categories.map((cat, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "name": cat.name,
        "description": cat.description,
        "url": `${SITE_URL}/catalog?q=${encodeURIComponent(cat.name)}`
      }))
    });
    document.head.appendChild(script);

    return () => {
      document.getElementById("schema-categories")?.remove();
    };
  }, []);

  const selectedCat = categories.find((c) => c.name === selected);

  return (
    <div className="min-h-screen bg-[#faf8f4]">
      <Navbar />

      {/* Header */}
      <div className="pt-24 pb-10 bg-[#3a2410]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[#f0c87a] text-xs font-semibold uppercase tracking-widest mb-4">
            <i className="ri-grid-line"></i>
            Categories
          </div>
          <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Browse by Category
          </h1>
          <p className="text-[#c9a87c]">Explore {categories.length} curated categories of junk journal and snail mail supplies.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Category Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelected(selected === cat.name ? null : cat.name)}
              className={`text-left rounded-2xl overflow-hidden border transition-all duration-300 cursor-pointer ${selected === cat.name ? "border-[#c9a87c] ring-2 ring-[#c9a87c]/30" : "border-[#ede5d8] hover:border-[#c9a87c]/50"} bg-white`}
            >
              <div className="h-36 overflow-hidden">
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 flex items-center justify-center text-[#8b5e3c]">
                    <i className={`${cat.icon} text-sm`}></i>
                  </div>
                  <h3 className="font-bold text-[#2c1e0f] text-sm">{cat.name}</h3>
                </div>
                <p className="text-xs text-[#7a6555] leading-relaxed mb-2 line-clamp-2">{cat.description}</p>
                <span className="text-xs font-semibold text-[#8b5e3c]">{cat.count} listings</span>
              </div>
            </button>
          ))}
        </div>

        {/* Selected Category Detail */}
        {selectedCat && (
          <div className="bg-white rounded-2xl border border-[#c9a87c]/40 p-8 mb-8">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-64 h-48 rounded-xl overflow-hidden shrink-0">
                <img
                  src={selectedCat.image}
                  alt={selectedCat.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 flex items-center justify-center text-[#8b5e3c]">
                    <i className={`${selectedCat.icon}`}></i>
                  </div>
                  <h2 className="text-2xl font-bold text-[#2c1e0f]" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {selectedCat.name}
                  </h2>
                </div>
                <p className="text-[#7a6555] mb-4 leading-relaxed">{selectedCat.description}</p>
                <div className="flex items-center gap-6 mb-6">
                  <div>
                    <div className="text-2xl font-bold text-[#8b5e3c]" style={{ fontFamily: "'Playfair Display', serif" }}>{selectedCat.count}</div>
                    <div className="text-xs text-[#9a8a78]">Total Listings</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#8b5e3c]" style={{ fontFamily: "'Playfair Display', serif" }}>10+</div>
                    <div className="text-xs text-[#9a8a78]">Creators</div>
                  </div>
                </div>
                <Link
                  to={`/catalog?q=${encodeURIComponent(selectedCat.name)}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#8b5e3c] text-white text-sm font-semibold hover:bg-[#7a5234] transition-colors whitespace-nowrap"
                >
                  Browse {selectedCat.name}
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
